"use client";

import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import { cn } from "./utils";
import { VisuallyHidden } from "./visually-hidden";

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-[90] bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className,
    )}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className,
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    fallbackTitle?: string;
  }
>(({ className, children, fallbackTitle = "Dialog", ...props }, ref) => {
  // Use a ref to avoid recalculating on every render.
  // useMemo([children]) recalculated every render since children is always
  // a new reference, which destabilized Radix's Presence component and
  // caused an infinite setState loop via ref callbacks (presence.tsx:159).
  const hasTitleRef = React.useRef(false);
  const hasCheckedRef = React.useRef(false);

  if (!hasCheckedRef.current) {
    const containsTitle = (node: React.ReactNode): boolean => {
      if (!React.isValidElement(node)) return false;
      const type: any = node.type;
      if (
        type === DialogTitle ||
        type?.displayName === DialogPrimitive.Title.displayName
      )
        return true;
      if ((node.props as React.PropsWithChildren)?.children) {
        return React.Children.toArray(
          (node.props as React.PropsWithChildren).children,
        ).some(containsTitle);
      }
      return false;
    };
    hasTitleRef.current = React.Children.toArray(children).some(containsTitle);
    hasCheckedRef.current = true;
  }

  return (
    <DialogPrimitive.Portal
      container={typeof document !== "undefined" ? document.body : undefined}
    >
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          "fixed left-[50%] top-[50%] z-[100] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
          className,
        )}
        {...props}
      >
        {!hasTitleRef.current && (
          <DialogHeader>
            <VisuallyHidden>
              <DialogTitle>{fallbackTitle}</DialogTitle>
            </VisuallyHidden>
          </DialogHeader>
        )}
        {/* Always render a hidden description to suppress Radix warnings.
            Conditional rendering here destabilizes Radix Presence and causes
            infinite setState loops (presence.tsx:159). This is zero-cost visually. */}
        <VisuallyHidden>
          <DialogPrimitive.Description>Dialog Content</DialogPrimitive.Description>
        </VisuallyHidden>
        {children}
        <DialogPrimitive.Close
          className="absolute right-3 top-3 lg:right-4 lg:top-4 z-10 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground p-1 lg:p-0"
        >
          <X className="h-6 w-6 lg:h-4 lg:w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
